import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  alpha,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CustomOtpInput from '../components/CustomOtpInput';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { loginThunk, updateTokens, clearSuccess, clearError, generateOtpThunk, verifyOtpThunk } from '../features/auth/authSlice';
import {bookAppointment} from '../features/booking/bookingSlice';
import UserNotFoundDialog from '../components/UserNotFoundDialog';
import axiosInstance from '../api/axiosInstance';
import { getBookingData, getPendingBooking, clearPendingBooking, clearBookingData } from '../utils/bookingStorage';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: theme.transitions.create(['box-shadow', 'border-color']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

// Custom OTP input styling
const getOtpInputStyle = (isMobile) => ({
  width: isMobile ? '2.5rem' : '2.8rem',
  height: isMobile ? '3rem' : '3.2rem',
  fontSize: isMobile ? '1.3rem' : '1.4rem',
});

const otpInputFocusStyle = {
  borderColor: '#1976d2',
  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  transform: 'scale(1.05)',
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, loading, errorMessage, successMessage } = useSelector((state) => state.auth);
  
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showUserNotFoundDialog, setShowUserNotFoundDialog] = useState(false);
  const [userNotFoundIdentifier, setUserNotFoundIdentifier] = useState('');

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccess());
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (errorMessage) {
      if (typeof errorMessage === 'object') {
        Object.keys(errorMessage).forEach((key) => {
          toast.error(`${key}: ${errorMessage[key].join(', ')}`);
        });
      } else {
        toast.error(errorMessage);
      }
      dispatch(clearError());
    }
  }, [errorMessage, dispatch]);

  // Check if there is saved booking data or pending booking
  const savedBookingData = getBookingData();
  const pendingBooking = getPendingBooking();

  // Redirect logged-in user to their dashboard or intended booking flow
  // if (user) {
  //   if (savedBookingData) {
  //     navigate('/book-appointment', { replace: true });
  //     toast.success('You are now logged in! Continue your booking.');
  //     return null;
  //   } else {
  //     // Check if the user is a doctor and needs to complete onboarding
  //     if (user.role === 'doctor' && !user.profile_completed) {
  //       return <Navigate to="/doctor-onboarding" replace />;
  //     }
  //     const dashboardPath = `/${user.role}-dashboard`;
  //     return <Navigate to={dashboardPath} replace />;
  //   }
  // }

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await dispatch(loginThunk({ identifier: contact, password })).unwrap();
      const { access, refresh, user } = response;
      dispatch(updateTokens({ accessToken: access, refreshToken: refresh, user }));

      // Check if there's saved booking data or pending booking
      const savedBookingData = getBookingData();
      const pendingBooking = getPendingBooking();
      
      // Handle pending booking from QuickBookingWidget
      if (pendingBooking) {
        try {
          const doctorId = pendingBooking.doctor_id || pendingBooking.doctorId;
          const response = await axiosInstance.post(
            `/doctors/${doctorId}/book/`,
            {
              scheduled_date: pendingBooking.appointmentData?.scheduled_date || pendingBooking.selectedDate,
              scheduled_time: pendingBooking.appointmentData?.scheduled_time || pendingBooking.selectedTime,
              type: pendingBooking.appointmentData?.type || (pendingBooking.consultationType === 'video' ? 'Video' : pendingBooking.consultationType === 'phone' ? 'Phone' : 'In-Person'),
              details: pendingBooking.appointmentData?.details || pendingBooking.reason || '',
              // Include clinic ID if available
              ...(pendingBooking.clinic_id && { clinic_id: pendingBooking.clinic_id }),
            }
          );
          
          clearPendingBooking();
          toast.success('Appointment booked successfully!');
          
          setTimeout(() => {
            if (user.role === 'patient') {
              navigate('/patient/appointments');
            } else {
              navigate(`/${user.role}-dashboard`);
            }
          }, 1500);
          return;
        } catch (bookingErr) {
          console.error('Booking error after login:', bookingErr);
          toast.error('Failed to complete booking. Please try again.');
          clearPendingBooking();
        }
      }
      
      // Handle saved booking data from BookAppointment page
      if (savedBookingData) {
        // If there's saved booking data, proceed with booking
        dispatch(
          bookAppointment({
            doctorId: savedBookingData.selectedDoctor,
            clinicId: savedBookingData.selectedClinic || null,
            scheduledDate: savedBookingData.selectedDate,
            scheduledTime: convertTo24Hour(savedBookingData.selectedSlot),
          })
        ).then((result) => {
          if (result.meta.requestStatus === 'fulfilled') {
            clearBookingData();
            toast.success('Welcome back! Your appointment has been booked.');
            setTimeout(() => {
              navigate(`/${user.role}-dashboard`);
            }, 2000);
          }
        });
      } else {
        // If no booking data, check if doctor needs onboarding
        if (user.role === 'doctor' && !user.profile_completed) {
          toast.success('Welcome! Let\'s complete your profile setup.');
          setTimeout(() => {
            navigate('/doctor-onboarding');
          }, 1000);
        } else {
          // Check if patient needs onboarding
          if (user.role === 'patient') {
            // Check if patient has completed onboarding
            const hasProfile = user.patient_profile_completed || false;
            if (!hasProfile) {
              toast.success('Welcome! Let\'s complete your profile setup.');
              setTimeout(() => {
                navigate('/onboarding/patient/');
              }, 1000);
            } else {
              // Patient has completed onboarding
              toast.success('Welcome back!');
              setTimeout(() => {
                navigate('/patient-dashboard');
              }, 1000);
            }
          } else {
            // Regular login flow for other roles
            toast.success('Welcome back!');
            setTimeout(() => {
              navigate(`/${user.role}-dashboard`);
            }, 1000);
          }
        }
      }
    } catch (err) {
      console.error('Login failed:', err);
      
      // Handle specific error cases
      const errorMessage = err?.non_field_errors?.[0] || 
                          err?.identifier?.[0] || 
                          err?.password?.[0] ||
                          err?.detail ||
                          'Login failed. Please try again.';
      
      toast.error(errorMessage);
    }
  };

  const handleSendOtp = async () => {
    if (!contact.trim()) {
      toast.error('Please enter your email or phone number');
      return;
    }

    try {
      await dispatch(generateOtpThunk(contact)).unwrap();
      setOtpSent(true);
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('OTP send error:', error);
      
      // Handle user not found case
      if (error.type === 'USER_NOT_FOUND') {
        setUserNotFoundIdentifier(contact);
        setShowUserNotFoundDialog(true);
      } else {
        // Handle other errors
        const errorMessage = error?.non_field_errors?.[0] || 
                            error?.error || 
                            'Failed to send OTP. Please try again.';
        toast.error(errorMessage);
      }
    }
  };

  const handleOtpLogin = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      const result = await dispatch(verifyOtpThunk({ identifier: contact, otp })).unwrap();
      
      // Update authentication state with tokens and user data
      if (result.access && result.refresh && result.user) {
        dispatch(updateTokens({
          user: result.user,
          accessToken: result.access,
          refreshToken: result.refresh
        }));
      }
      
      // Check for pending booking after OTP login
      const pendingBooking = JSON.parse(localStorage.getItem('pendingBooking'));
      if (pendingBooking) {
        try {
          const bookingResponse = await axiosInstance.post(
            `/doctors/${pendingBooking.doctor_id}/book/`,
            {
              scheduled_date: pendingBooking.appointmentData.scheduled_date,
              scheduled_time: pendingBooking.appointmentData.scheduled_time,
              type: pendingBooking.appointmentData.type,
              details: pendingBooking.appointmentData.details,
            }
          );
          
          clearPendingBooking();
          toast.success('Appointment booked successfully!');
          
          setTimeout(() => {
            if (result.user?.role === 'patient') {
              navigate('/patient/appointments');
            } else {
              navigate(`/${result.user?.role}-dashboard`);
            }
          }, 1500);
          return;
        } catch (bookingErr) {
          console.error('Booking error after OTP login:', bookingErr);
          toast.error('Failed to complete booking. Please try again.');
          clearPendingBooking();
        }
      }
      
      // Check if patient needs onboarding
      if (result.user?.role === 'patient' && !result.user?.patient_profile_completed) {
        toast.success('Welcome! Let\'s complete your profile setup.');
        setTimeout(() => {
          navigate('/onboarding/patient/');
        }, 1000);
      } else {
        toast.success('Welcome back!');
        setTimeout(() => {
          navigate(`/${result.user?.role}-dashboard`);
        }, 1000);
      }
    } catch (error) {
      console.error('OTP login error:', error);
      
      // Handle specific error cases
      const errorMessage = error?.non_field_errors?.[0] || 
                          error?.otp?.[0] || 
                          error?.identifier?.[0] ||
                          'Invalid OTP. Please try again.';
      
      toast.error(errorMessage);
      
      // Clear OTP on error
      setOtp('');
    }
  };

  // Auto-submit OTP when 6 digits are entered
  useEffect(() => {
    if (loginMethod === 'otp' && otp.length === 6 && otpSent) {
      setTimeout(() => handleOtpLogin(), 300);
    }
  }, [otp, loginMethod, otpSent]);

  const convertTo24Hour = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(/[: ]/);
    const period = time.includes('PM') ? 'PM' : 'AM';
    let hours24 = parseInt(hour, 10);
    if (period === 'PM' && hours24 !== 12) {
      hours24 += 12;
    }
    if (period === 'AM' && hours24 === 12) {
      hours24 = 0;
    }
    return `${hours24.toString().padStart(2, '0')}:${minute}`;
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin(event);
    }
  };

  const renderError = (error) => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error.non_field_errors) {
      return error.non_field_errors.join(', ');
    }
    return 'An unknown error occurred.';
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
      <Fade in timeout={1000}>
        <Box>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            align="center"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4
            }}
          >
            Welcome Back 👋
          </Typography>

          <StyledPaper elevation={0}>
            {errorMessage && (
              <Fade in>
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  {renderError(errorMessage)}
                </Alert>
              </Fade>
            )}

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Login Method Toggle */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <StyledButton
                  variant={loginMethod === 'password' ? 'contained' : 'outlined'}
                  onClick={() => setLoginMethod('password')}
                  sx={{ 
                    flex: 1,
                    background: loginMethod === 'password' ? 
                      `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` : 
                      'transparent'
                  }}
                >
                  Password
                </StyledButton>
                <StyledButton
                  variant={loginMethod === 'otp' ? 'contained' : 'outlined'}
                  onClick={() => setLoginMethod('otp')}
                  sx={{ 
                    flex: 1,
                    background: loginMethod === 'otp' ? 
                      `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` : 
                      'transparent'
                  }}
                >
                  OTP
                </StyledButton>
              </Box>

              <StyledTextField
                fullWidth
                label="Email or Phone"
                variant="outlined"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Enter your email or phone"
                onKeyPress={handleKeyPress}
              />

              {loginMethod === 'password' ? (
                <StyledTextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {!otpSent ? (
                    <StyledButton
                      fullWidth
                      variant="outlined"
                      onClick={handleSendOtp}
                      disabled={loading || !contact.trim()}
                    >
                      Send OTP
                    </StyledButton>
                  ) : (
                    <>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Enter the 6-digit code sent to {contact}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CustomOtpInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={6}
                          shouldAutoFocus={true}
                          inputStyle={getOtpInputStyle(isMobile)}
                          focusStyle={otpInputFocusStyle}
                        />
                      </Box>
                      <StyledButton
                        fullWidth
                        variant="outlined"
                        onClick={handleSendOtp}
                        disabled={loading}
                        size="small"
                      >
                        Resend OTP
                      </StyledButton>
                    </>
                  )}
                </Box>
              )}

              <StyledButton
                fullWidth
                variant="contained"
                onClick={loginMethod === 'password' ? handleLogin : handleOtpLogin}
                disabled={loading || (loginMethod === 'otp' && (!otpSent || otp.length !== 6))}
                sx={{ 
                  mt: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                {loading ? 'Logging in...' : loginMethod === 'password' ? 'Login' : 'Verify OTP'}
              </StyledButton>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Don't have an account?{' '}
                  <Button
                    color="primary"
                    onClick={() => navigate('/signup')}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'transparent',
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Sign up
                  </Button>
                </Typography>
              </Box>
            </Box>
          </StyledPaper>
        </Box>
      </Fade>

      <ToastContainer 
        position="bottom-center" 
        autoClose={3000}
        theme="colored"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* User Not Found Dialog */}
      <UserNotFoundDialog
        open={showUserNotFoundDialog}
        onClose={() => setShowUserNotFoundDialog(false)}
        identifier={userNotFoundIdentifier}
        onSuccess={(result) => {
          // After successful signup, OTP is automatically sent
          if (result.otp_sent) {
            setContact(userNotFoundIdentifier);
            setOtpSent(true);
            setLoginMethod('otp'); // Switch to OTP mode
            toast.success(`Account created! OTP sent to your ${result.otp_sent_to}.`, {
              icon: '🎉'
            });
          } else {
            toast.success('Account created successfully! Please request OTP for verification.', {
              icon: '🎉'
            });
          }
        }}
      />
    </Container>
  );
};

export default Login;
